// Client for the admin-side management API (/api/external-api on BCC.ADMIN.API):
// list / mint / revoke the external read-only tokens and read call logs + stats.
//
// IMPORTANT: this goes through the shared `axios` instance (NOT raw fetch) so the
// Auth0AxiosBridge request interceptor attaches the admin Bearer token : a raw
// fetch is invisible to that interceptor and gets 401 when BYPASS_AUTH is off.
import axios from "axios";

export interface ExternalApiToken {
	id: string;
	name: string;
	prefix: string;
	createdByLabel?: string | null;
	createdAt: string;
	expiresAt?: string | null;
	revokedAt?: string | null;
	lastUsedAt?: string | null;
	isLive: boolean;
}

export interface CreateTokenResponse {
	token: string; // plaintext : shown once
	id: string;
	name: string;
	prefix: string;
	expiresAt?: string | null;
}

export interface ExternalApiLog {
	id: number;
	tokenName?: string | null;
	method: string;
	path: string;
	queryString?: string | null;
	statusCode: number;
	durationMs: number;
	ipAddress?: string | null;
	createdAt: string;
}

export interface ExternalApiLogPage {
	total: number;
	page: number;
	pageSize: number;
	items: ExternalApiLog[];
}

export interface ExternalApiStats {
	total: number;
	last24h: number;
	last7d: number;
	last30d: number;
	ok: number;
	clientErrors: number;
	serverErrors: number;
	activeTokens: number;
}

const apiBase = (process.env.REACT_APP_API_URL ?? "").replace(/\/+$/, "");
const base = `${apiBase}/api/external-api`;

function toError(e: any): Error {
	const data = e?.response?.data;
	const msg =
		(typeof data === "string" && data) ||
		data?.message ||
		e?.response?.statusText ||
		e?.message ||
		(e?.response?.status ? `HTTP ${e.response.status}` : "Erreur réseau");
	return new Error(typeof msg === "string" ? msg : "Erreur");
}

export const externalApiClient = {
	listTokens: async (): Promise<ExternalApiToken[]> => {
		try {
			const r = await axios.get<ExternalApiToken[]>(`${base}/tokens`, { withCredentials: true });
			return r.data;
		} catch (e) { throw toError(e); }
	},

	createToken: async (name: string, expiresInDays?: number | null): Promise<CreateTokenResponse> => {
		try {
			const r = await axios.post<CreateTokenResponse>(
				`${base}/tokens`,
				{ name, expiresInDays: expiresInDays ?? null },
				{ withCredentials: true },
			);
			return r.data;
		} catch (e) { throw toError(e); }
	},

	revokeToken: async (id: string): Promise<void> => {
		try {
			await axios.delete(`${base}/tokens/${encodeURIComponent(id)}`, { withCredentials: true });
		} catch (e) { throw toError(e); }
	},

	logs: async (page = 1, pageSize = 50): Promise<ExternalApiLogPage> => {
		try {
			const r = await axios.get<ExternalApiLogPage>(
				`${base}/logs?page=${page}&pageSize=${pageSize}`,
				{ withCredentials: true },
			);
			return r.data;
		} catch (e) { throw toError(e); }
	},

	stats: async (): Promise<ExternalApiStats> => {
		try {
			const r = await axios.get<ExternalApiStats>(`${base}/stats`, { withCredentials: true });
			return r.data;
		} catch (e) { throw toError(e); }
	},
};
